import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import googleLg from '../../assets/images/google-logo.png';
import { keyStorage, ROUTES } from '@/configs/constants';
import { roleIdEnum } from '@/types/enums';
import { getStorage } from '@/utils/browsers';
import { Button } from '@/components/common/Button';
import { ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Profile } from '@/types/auth';
import GoogleAPI from '@/services/drive';
import { t } from 'i18next';
import logo from '../../assets/images/logo_red.png';
import { flowResult } from 'mobx';

export default observer(function LoginPage() {
    const googleAPIService = GoogleAPI.getInstance();
    const navigate = useNavigate();
    const [currentCode, setCurrentCode] = useState<string>('');
    const {
        authStore: { setAvatar, setToken, setProfile, googleLogin, setAuthGG },
        modalStore: { showErrorModal },
        apiStore: { setLoading }
    } = useStore();

    useEffect(() => {
        checkAuth(false, true);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        if (!code || !state) {
            return;
        }
        (async () => {
            if (code !== currentCode) {
                setLoading(true);
                setCurrentCode(code);
                setTimeout(() => {
                    chechGGAuth(code, state);
                }, 1000);
            }
        })();
    }, [navigate]);

    const chechGGAuth = async (code: string, state: string) => {
        const res = await flowResult(googleLogin({ code, state }));
        if (res && res.ok) {
            const { token, user } = res.data;
            if (user.role_id || user.role_id === roleIdEnum.adminSystem) {
                setAvatar(user.picture!);
                setToken(token!);
                setProfile(user);
                setAuthGG(user.access_token, state);
                if (user.role_id === roleIdEnum.adminSystem) {
                    navigate(ROUTES.companyManager.href);
                } else if (user.role_id === roleIdEnum.adminCompany) {
                    navigate(ROUTES.userManagement.href);
                }
            } else {
                showErrorModal({
                    content: t('messages.permission_access_cms_fail')
                });
            }
        }
    };

    function buildAuthUrl(state: string) {
        const params = new URLSearchParams({
            client_id: process.env.REACT_APP_CLIENT_ID!,
            redirect_uri: process.env.REACT_APP_REDIRECT_URI!,
            response_type: 'code',
            scope: process.env.REACT_APP_SCOPES!,
            access_type: 'offline',
            prompt: 'consent',
            include_granted_scopes: 'true',
            state
        });
        return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    }

    const handleSignInGG = async () => {
        const state = Math.random().toString(36).slice(2);
        localStorage.setItem('oauth_state', state);
        window.location.assign(buildAuthUrl(state));
    };

    const checkAuth = async (isFromBtnLogin = false, isAutoLogin = false): Promise<boolean | void> => {
        const ggToken = await googleAPIService.getToken();
        if (!ggToken) {
            return false;
        }
        const token = getStorage(keyStorage.TOKEN);
        const profile: Profile = getStorage(keyStorage.PROFILE, true);
        if (token && profile && profile.email && ggToken) {
            setToken(token);
            setProfile(profile);
            const role_id = profile?.role_id;
            if (role_id === roleIdEnum.adminSystem) {
                navigate(ROUTES.companyManager.href);
            } else if (role_id === roleIdEnum.adminCompany) {
                navigate(ROUTES.userManagement.href);
            }
            if (isFromBtnLogin) {
                return true;
            }
        }
        return false;
    };

    return (
        <div className=' w-full h-full flex flex-col justify-center items-center '>
            <div className='w-[544px] h-[351px] flex items-center justify-center flex-col rounded-xl bg-white'>
                <img src={logo} className='w-[90%] container' alt={logo} />
                <h1 className='m-8 text-[32px] font-bold'>{t('general.brand_name')}</h1>
                <Button
                    variant='outline'
                    onClick={() => handleSignInGG()}
                    className='flex items-center rounded-xl shadow-md px-5 w-[85%] justify-center h-[65px] border-[1px] border-gray-600'
                >
                    <img src={googleLg} alt={googleLg} className='w-[26px] h-[26px] mr-4' />
                    <span className='mt-1 text-[22px] font-normal text-gray-700'>
                        {t('messages.google_login')}
                    </span>
                </Button>
            </div>
            <div className='absolute bottom-2 right-4 text-white text-4 font-medium flex items-center space-x-1 cursor-pointer hover:underline'>
                <ShieldAlert className='w-6 h-6' />
                <a href='https://cms.onlinepost.jp/policy.html'>{t('text.privacy_policy')}</a>
            </div>
        </div>
    );
});
